import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useStore } from './store/useStore';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { CheckoutSuccess } from './pages/Billing/CheckoutSuccess';
import { CheckoutFailure } from './pages/Billing/CheckoutFailure';

// Lazy load components with descriptive chunk names
const HomePage = React.lazy(() => import(/* webpackChunkName: "home" */ './pages/HomePage'));
// const TodoList = React.lazy(() => import(/* webpackChunkName: "todo" */ './pages/TodoList'));
const Deadlines = React.lazy(() => import(/* webpackChunkName: "deadlines" */ './pages/Deadlines'));
// Schedule routes
const WeeklySchedule = React.lazy(() => import(/* webpackChunkName: "schedule-weekly" */ './pages/Schedule/WeeklySchedule'));
const MonthlySchedule = React.lazy(() => import(/* webpackChunkName: "schedule-monthly" */ './pages/Schedule/MonthlySchedule'));
const YearlySchedule = React.lazy(() => import(/* webpackChunkName: "schedule-yearly" */ './pages/Schedule/YearlySchedule'));

// Task and Project Management
const Progress = React.lazy(() => import(/* webpackChunkName: "progress" */ './pages/Progress'));
const TaskManagement = React.lazy(() => import(/* webpackChunkName: "task-management" */ './pages/TaskManagement'));
const Projects = React.lazy(() => import(/* webpackChunkName: "projects" */ './pages/Projects'));
const ProjectDetail = React.lazy(() => import(/* webpackChunkName: "project-detail" */ './pages/ProjectDetail'));
const ArchivedProjects = React.lazy(() => import(/* webpackChunkName: "archived-projects" */ './pages/ArchivedProjects'));

// Authentication
const Login = React.lazy(() => import(/* webpackChunkName: "auth-login" */ './pages/Auth/Login'));
const Signup = React.lazy(() => import(/* webpackChunkName: "auth-signup" */ './pages/Auth/Signup'));

// User Management
const Profile = React.lazy(() => import(/* webpackChunkName: "user-profile" */ './pages/Profile/Profile'));
const Settings = React.lazy(() => import(/* webpackChunkName: "user-settings" */ './pages/Settings/Settings'));
const Billing = React.lazy(() => import(/* webpackChunkName: "user-billing" */ './pages/Billing/Billing'));

// Support and Information
const Support = React.lazy(() => import(/* webpackChunkName: "support" */ './pages/Support/Support'));
const About = React.lazy(() => import(/* webpackChunkName: "about" */ './pages/About/About'));

// Legal
const Terms = React.lazy(() => import(/* webpackChunkName: "legal-terms" */ './pages/Legal/Terms'));
const Privacy = React.lazy(() => import(/* webpackChunkName: "legal-privacy" */ './pages/Legal/Privacy'));

const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation').then(module => ({ default: module.AcceptInvitation })));

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  const { user } = useStore();

  return (
    <DarkModeProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <React.Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
            <Route path="/signup" element={user ? <Navigate to="/" replace /> : <Signup />} />
            <Route path="invite/:token" element={<AcceptInvitation />} />
            
            {/* Legal routes - publicly accessible */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />

            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
              {/* Dashboard */}
              <Route index element={<HomePage />} />
              
              {/* Task Management */}
              {/* <Route path="todo" element={<TodoList />} /> */}
              <Route path="tasks" element={<TaskManagement />} />
              <Route path="deadlines" element={<Deadlines />} />

              {/* Schedule Management */}
              <Route path="schedule">
                <Route index element={<WeeklySchedule />} />
                <Route path="month" element={<MonthlySchedule />} />
                <Route path="year" element={<YearlySchedule />} />
                <Route path="archived-projects" element={<ArchivedProjects />} />
              </Route>
              
              {/* Project Management */}
              <Route path="projects">
                <Route index element={<Projects />} />
                <Route path=":projectId" element={<ProjectDetail />} />
              </Route>
              
              {/* Progress Tracking */}
              <Route path="progress" element={<Progress />} />
              
              {/* User Management */}
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
              <Route path="billing" element={<Billing />} />
              
              {/* Support */}
              <Route path="support" element={<Support />} />
            </Route>

            {/* Add these routes inside your Routes component */}
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/failure" element={<CheckoutFailure />} />

            {/* Catch all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </Router>
    </DarkModeProvider>
  );
}

export default App;